import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Flex, Tooltip } from 'antd';
import { IoClose, IoEyeOutline, IoSearchOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router';

import BaseText from '@components/typography/BaseText';
import { useProjectMode } from '@contexts/ProjectMode.context';
import { useShowCollectionResults } from '@hooks/useShowCollectionResults';
import { PROJECT_VIEW_MODE } from '@utils/constants';
import { buildProjectUrl } from '@utils/projectPath';
import { getFindCollections } from '@utils/api/find';

import { Line, Note, PanelRow } from './FindPanel';
import {
	collectionNote,
	collectionQuestion,
	objectNames,
	splitCollections,
} from './collectionSteps';

// More collections than this fold away behind "Show all"
const FOLDED = 3;

export const findCollectionsKey = projectUuid => ['find', 'collections', projectUuid];

// The collection's note: one line, cut short rather than running under the buttons
const noteStyle = { overflow: 'hidden', textOverflow: 'ellipsis' };

/**
 * Show a collection's results on the map. In the collection view it is selected as the Analyzed
 * Collections picker selects it; the model-based view has no collection layers, so it goes to the
 * collection view with the collection selected.
 * @param {string} projectUuid
 * @returns {(collectionUuid: string) => void}
 */
export const useViewCollection = projectUuid => {
	const mode = useProjectMode();
	const navigate = useNavigate();
	const showResults = useShowCollectionResults();
	return useCallback(
		collectionUuid => {
			if (mode === PROJECT_VIEW_MODE.COLLECTION || !projectUuid) {
				showResults(collectionUuid);
				return;
			}
			const query = new URLSearchParams({ collection_uuid: collectionUuid });
			navigate(`${buildProjectUrl(projectUuid, PROJECT_VIEW_MODE.COLLECTION)}?${query}`);
		},
		[mode, navigate, projectUuid, showResults]
	);
};

/**
 * The collections Find offers in the project (`GET /find/collections`), asked while Find is open.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export const useFindCollections = ({ projectUuid, open }) =>
	useQuery({
		queryKey: findCollectionsKey(projectUuid),
		queryFn: () => getFindCollections({ projectUuid }),
		enabled: Boolean(open && projectUuid),
		staleTime: 30000,
		retry: false,
	});

const CollectionLine = ({ collection, chosen, onFind, onView }) => (
	<Line>
		<Flex vertical style={{ minWidth: 0, flex: 1 }}>
			<BaseText ellipsis strong={chosen}>
				{collection.name}
			</BaseText>
			<Tooltip title={objectNames(collection)}>
				<Note style={noteStyle}>{collectionNote(collection)}</Note>
			</Tooltip>
		</Flex>
		<Flex gap="small">
			{collection.analyzed && (
				<Tooltip title="Show its results on the map">
					<Button
						type="text"
						size="small"
						icon={<IoEyeOutline />}
						aria-label={`View ${collection.name}`}
						onClick={() => onView(collection)}
					/>
				</Tooltip>
			)}
			<Tooltip
				title={
					collection.searches?.length
						? `Find ${objectNames(collection)} here`
						: 'It has no objects to find'
				}>
				<Button
					type={chosen ? 'primary' : 'text'}
					size="small"
					icon={<IoSearchOutline />}
					disabled={!collection.searches?.length}
					aria-label={`Find ${collection.name}`}
					aria-pressed={chosen}
					onClick={() => onFind(collection)}
				/>
			</Tooltip>
		</Flex>
	</Line>
);

/**
 * Collections: the user's collections of objects, the ones analyzed in this project first. Find on
 * one puts its objects in the search box and asks to start; the results then save into it. View
 * shows an analyzed one's results on the map, as the Analyzed Collections picker does.
 * @param {Object} props
 * @param {string} props.projectUuid
 * @param {boolean} props.open
 * @param {string|null} props.chosenUuid - The collection Find is working for
 * @param {Function} props.onFind - (collection) → void
 */
export const FindCollections = ({ projectUuid, open, chosenUuid, onFind }) => {
	const list = useFindCollections({ projectUuid, open });
	const viewCollection = useViewCollection(projectUuid);
	const [unfolded, setUnfolded] = useState(false);
	const collections = list.data?.collections ?? [];
	if (list.isError) {
		return (
			<PanelRow label="Collections">
				<Note role="alert">Your collections could not be loaded just now.</Note>
			</PanelRow>
		);
	}
	if (collections.length === 0) return null;
	const { analyzed, others } = splitCollections(collections);
	const ordered = [...analyzed, ...others];
	const listed = unfolded ? ordered : ordered.slice(0, FOLDED);
	const line = collection => (
		<CollectionLine
			key={collection.uuid}
			collection={collection}
			chosen={collection.uuid === chosenUuid}
			onFind={onFind}
			onView={c => viewCollection(c.uuid)}
		/>
	);
	return (
		<PanelRow label="Collections">
			{listed.map(line)}
			{ordered.length > FOLDED && (
				<Flex>
					<Button
						type="link"
						size="small"
						onClick={() => setUnfolded(current => !current)}>
						{unfolded ? 'Show fewer' : `Show all ${ordered.length}`}
					</Button>
				</Flex>
			)}
		</PanelRow>
	);
};

/**
 * The question asked once a collection is chosen: find its objects here? Start runs the search in
 * the box; Not now lets the collection go. After a search of a collection with more (roads apart
 * from objects), Next puts the next one in the box.
 * @param {Object} props
 * @param {Object} props.target - `{ collection, step }`
 * @param {string} props.where - "this view" | "the drawn area"
 * @param {boolean} props.ran - The search of this step ran (its results are on the map)
 * @param {boolean} props.busy - A search runs, or Find cannot start one now
 * @param {boolean} [props.computeReady] - Compute is up; else Start starts it and the search follows
 * @param {Function} props.onStart
 * @param {Function} props.onNext
 * @param {Function} props.onCancel
 */
export const CollectionAsk = ({
	target,
	where,
	ran,
	busy,
	computeReady = true,
	queued = false,
	onStart,
	onNext,
	onCancel,
}) => {
	const { title, detail } = collectionQuestion(target, where);
	const searches = target.collection.searches ?? [];
	const hasNext = target.step + 1 < searches.length;
	return (
		<PanelRow label="Collection">
			<Line>
				<BaseText ellipsis strong>
					{target.collection.name}
				</BaseText>
				<Tooltip title="Stop finding for this collection">
					<Button
						type="text"
						size="small"
						icon={<IoClose />}
						aria-label={`Stop finding for ${target.collection.name}`}
						onClick={onCancel}
					/>
				</Tooltip>
			</Line>
			{!ran && (
				<>
					<BaseText>{title}</BaseText>
					{detail && <Note>{detail}</Note>}
					<Flex gap="small">
						<Button
							type="primary"
							onClick={onStart}
							loading={queued}
							disabled={busy || queued || !searches.length}>
							{queued
								? 'Starting compute…'
								: computeReady
									? 'Start Find'
									: 'Start compute and Find'}
						</Button>
						<Button onClick={onCancel}>Not now</Button>
					</Flex>
				</>
			)}
			{ran && (
				<>
					<Note>Save the results below to put them in {target.collection.name}.</Note>
					{hasNext && (
						<Button onClick={onNext} disabled={busy}>
							Next: {searches[target.step + 1].text}
						</Button>
					)}
				</>
			)}
		</PanelRow>
	);
};
